import React from 'react';
import styled from 'styled-components';
import { Empty } from 'antd';
import { useDashboardCards } from '../../../core/modules/pipelines/hooks';
import { IPipelineSummary } from '../../../core/modules/pipelines/pipelines.model';
import OverviewStats from './OverviewStats';
import PipelineCard from './PipelineCard';
import { PipelineGridSkeleton } from './skeletons';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const GroupSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const GroupTitle = styled.h3`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Count = styled.span`
  background: #f0f0f0;
  color: rgba(0, 0, 0, 0.55);
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
`;

const GROUPS: Array<{ label: string; predicate: (p: IPipelineSummary) => boolean }> = [
  {
    label: 'ETL',
    predicate: (p) => p.id.startsWith('durable.etl.') || p.id.startsWith('function.etl.')
  },
  { label: 'Backend', predicate: (p) => p.id.startsWith('function.backend.') },
  { label: 'Inference', predicate: (p) => p.id.startsWith('durable.inference.') },
  { label: 'Model Training', predicate: (p) => p.id.startsWith('azureml.') }
];

interface IPipelineGridProps {
  onSelect: (id: string) => void;
}

const PipelineGrid: React.FC<IPipelineGridProps> = ({ onSelect }) => {
  const { summaries, isLoading, isFetching, isError } = useDashboardCards();

  if (isLoading) {
    return <PipelineGridSkeleton />;
  }

  if (isError || summaries.length === 0) {
    return <Empty description='Failed to load pipelines' />;
  }

  return (
    <Wrapper>
      <OverviewStats summaries={summaries} />
      {GROUPS.map((group) => {
        const items = summaries.filter(group.predicate);

        if (items.length === 0) {
          return null;
        }

        return (
          <GroupSection key={group.label}>
            <GroupTitle>
              {group.label}
              <Count>{items.length}</Count>
            </GroupTitle>
            <Grid>
              {items.map((p) => (
                <PipelineCard
                  key={p.id}
                  summary={p}
                  isFetching={isFetching}
                  onSelect={onSelect}
                />
              ))}
            </Grid>
          </GroupSection>
        );
      })}
    </Wrapper>
  );
};

export default PipelineGrid;
